import { isBrowserStorageAvailable as isBrowser } from "~/lib/utils/safe-storage";

import { EMPTY_LEARNING_CENTER_DATA, type LearningCenterData } from "./types";

// Guest (logged-out) Learning Center persistence + the guest→profile merge,
// ported from hec-composable (`overrides/app/utils/helpers.js`). SSR-safe I/O
// layer only — same shape as `guest-save-for-later-utils`. Same-tab reactivity
// is owned by `useLearningCenter`. (SFCC-11502 / ARTICLES-11.)

export const GUEST_LEARNING_CENTER_KEY = "guestLearningCenterData";
export const LEARNING_CENTER_CONSENT_KEY = "tpaLearningCenterConsentData";

/** Reads guest progress; returns the empty seed on absence / corruption. */
export function getGuestLearningData(): NonNullable<LearningCenterData> {
  if (!isBrowser()) return { ...EMPTY_LEARNING_CENTER_DATA };
  try {
    const raw = localStorage.getItem(GUEST_LEARNING_CENTER_KEY);
    if (!raw) return { ...EMPTY_LEARNING_CENTER_DATA };
    const parsed = JSON.parse(raw) as LearningCenterData;
    // Lenient guard — `goals` must be an array, else treat as corrupt.
    if (!parsed || !Array.isArray(parsed.goals)) {
      console.warn("[learning-center] guest localStorage corrupt — resetting");
      localStorage.removeItem(GUEST_LEARNING_CENTER_KEY);
      return { ...EMPTY_LEARNING_CENTER_DATA };
    }
    return parsed as NonNullable<LearningCenterData>;
  } catch (error) {
    console.error("[learning-center] error reading guest data:", error);
    try {
      localStorage.removeItem(GUEST_LEARNING_CENTER_KEY);
    } catch {
      /* ignore */
    }
    return { ...EMPTY_LEARNING_CENTER_DATA };
  }
}

export function saveGuestLearningData(data: LearningCenterData): void {
  if (!isBrowser() || !data) return;
  try {
    localStorage.setItem(GUEST_LEARNING_CENTER_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("[learning-center] error writing guest data:", error);
  }
}

export function clearGuestLearningData(): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(GUEST_LEARNING_CENTER_KEY);
  } catch (error) {
    console.error("[learning-center] error clearing guest data:", error);
  }
}

export function getLearningCenterConsent(): boolean | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(LEARNING_CENTER_CONSENT_KEY);
    return raw ? (JSON.parse(raw) as boolean) : null;
  } catch {
    return null;
  }
}

export function saveLearningCenterConsent(data: boolean): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(LEARNING_CENTER_CONSENT_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("[learning-center] error writing consent:", error);
  }
}

/**
 * Merge guest progress into the registered profile data on login (port of
 * composable `mergeLearningCenterData`). Additive: adds goals/levels/modules
 * missing from the profile; for a shared module, replaces lessons only when the
 * guest has MORE (the aggressive-but-intentional composable rule). `activeGoal`
 * prefers the guest's; consent comes from the passed value.
 */
export function mergeLearningCenterData(
  registeredData: LearningCenterData,
  guestData: LearningCenterData,
  tpaLearningCenterConsentData: boolean | null,
): LearningCenterData {
  if (!guestData) return registeredData;
  if (!registeredData) return guestData;

  // Deep clone to avoid mutating the source.
  const merged = JSON.parse(
    JSON.stringify(registeredData),
  ) as NonNullable<LearningCenterData>;

  guestData.goals.forEach(guestGoal => {
    const existingGoal = merged.goals.find(g => g.id === guestGoal.id);
    if (!existingGoal) {
      merged.goals.push(guestGoal);
      return;
    }
    guestGoal.levels.forEach(guestLevel => {
      const existingLevel = existingGoal.levels.find(
        l => l.id === guestLevel.id,
      );
      if (!existingLevel) {
        existingGoal.levels.push(guestLevel);
        return;
      }
      guestLevel.modules.forEach(guestModule => {
        const existingModule = existingLevel.modules.find(
          m => m.id === guestModule.id,
        );
        if (!existingModule) {
          existingLevel.modules.push(guestModule);
          return;
        }
        const guestLessonCount = guestModule.lessons?.length ?? 0;
        const existingLessonCount = existingModule.lessons?.length ?? 0;
        if (guestLessonCount > existingLessonCount) {
          existingModule.lessons = guestModule.lessons;
        }
      });
    });
  });

  return {
    activeGoal: guestData.activeGoal || registeredData.activeGoal,
    goals: merged.goals,
    tpaLearningCenterConsentData,
  };
}
